import { useLayoutEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { Direction, IconButton, Stack, Tooltip } from '@mui/material';
import { KeyboardArrowRight as ArrowForwardIcon, KeyboardArrowLeft as ArrowBackIcon } from '@mui/icons-material';

import { OffsetLimit } from '@playq/services-shared';

import { usePersistedQuery } from '/hooks';
import { PersistedQueryKeys } from '/common/models';
import { TypographyGrey } from '/styles/shared/styledText';

type PaginationHookResult<T> = {
  entities: T[];
  total: number;
  fetchStatus: string;
  nextIterator?: OffsetLimit;
};

type PaginationHook<T, TArgs extends readonly unknown[]> = (
  ...args: [...TArgs, OffsetLimit, undefined, undefined, { enabled?: boolean; enablePrefetch?: boolean }]
) => PaginationHookResult<T>;

const getLocalIndex = (idx: number, offset: number) => idx - offset;

export const usePaginationView = <T extends { id: { id: string } }, TArgs extends unknown[]>({
  persistKey,
  id,
  useHook,
  customArgs,
}: {
  persistKey: PersistedQueryKeys | keyof typeof PersistedQueryKeys;
  customArgs: TArgs;
  id: string;
  useHook: PaginationHook<T, TArgs>;
}) => {
  const [pendingNavigateToIndex, setPendingNavigateToIndex] = useState<{ idx: number; direction: Direction } | null>(
    null
  );
  const [query, setQuery] = usePersistedQuery(persistKey);
  const history = useHistory();

  const { entities, total, fetchStatus, nextIterator } = useHook(...customArgs, query.iterator, undefined, undefined, {
    enablePrefetch: false,
    enabled: pendingNavigateToIndex !== null,
  });
  const { offset, limit } = query.iterator;

  const ids = useMemo(() => entities?.map((e) => e.id) ?? [], [entities]);
  const currentIndex = ids.findIndex((i) => i.id === id);
  const globalIndex = pendingNavigateToIndex?.idx ?? offset + currentIndex;

  const isLoading = fetchStatus === 'fetching';
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < ids.length - 1 || offset + ids.length < total;

  const displayCount = hasPrevious || hasNext;
  const nextLoading = isLoading && pendingNavigateToIndex?.direction === 'rtl';
  const prevLoading = isLoading && pendingNavigateToIndex?.direction === 'ltr';

  const prevIterator = new OffsetLimit({
    offset: Math.max(offset - limit, 0),
    limit,
  });
  const currentNextIterator =
    nextIterator ??
    new OffsetLimit({
      offset: offset + limit,
      limit,
    });

  useLayoutEffect(() => {
    if (pendingNavigateToIndex !== null && !isLoading) {
      const localIndex = getLocalIndex(pendingNavigateToIndex.idx, offset);

      if (ids[localIndex]?.id) {
        history.push(ids[localIndex].id);
        setPendingNavigateToIndex(null);
      }
    }
  }, [pendingNavigateToIndex, ids, offset, history, isLoading]);

  const handleNextClick = () => {
    const nextIndex = globalIndex + 1;
    const localIndex = getLocalIndex(nextIndex, offset);

    if (ids[localIndex] !== undefined) {
      history.push(ids[localIndex].id);
    } else if (ids.length < total) {
      setPendingNavigateToIndex({ idx: nextIndex, direction: 'rtl' });
      setQuery({
        ...query,
        iterator: currentNextIterator,
      });
    }
  };

  const handlePrevClick = () => {
    const prevIndex = globalIndex - 1;
    const localIndex = prevIndex - offset;

    if (prevIndex >= offset && ids[localIndex] !== undefined) {
      history.push(ids[localIndex].id);
    } else if (offset > 0) {
      setPendingNavigateToIndex({ idx: prevIndex, direction: 'ltr' });
      setQuery({
        ...query,
        iterator: prevIterator,
      });
    }
  };

  const node = (
    <Stack direction='row' alignItems='center' gap={1}>
      {displayCount && (
        <TypographyGrey variant='body2'>
          {globalIndex + 1} of {total}
        </TypographyGrey>
      )}
      <div>
        <Tooltip title='Newer' placement='bottom'>
          <IconButton disabled={(!hasPrevious && offset === 0) || prevLoading} onClick={handlePrevClick}>
            <ArrowBackIcon fontSize='small' />
          </IconButton>
        </Tooltip>

        <Tooltip title='Older' placement='bottom'>
          <IconButton disabled={!hasNext || nextLoading} onClick={handleNextClick}>
            <ArrowForwardIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </div>
    </Stack>
  );

  return node;
};
